import { css } from '@styled-system/css';
import { PackageOpen } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/shared/components/ui/Spinner';
import type { ProductItemData } from '@/shared/types/product';

import { ProductFeedCard } from './ProductFeedCard';

// ============================================================================
// Liste de cartes produit façon feed Autocop — présentationnelle (les
// filtres/tri/recherche restent dans le wrapper de la feature).
// ============================================================================

// Grille responsive — les container queries des cartes basculent
// automatiquement en layout compact quand la colonne est étroite.
const list = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
  gap: '5',
  width: '100%',
  alignItems: 'stretch',
});

const emptyState = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4',
  paddingY: '20',
  color: 'text.tertiary',
  textAlign: 'center',
});

const emptyTitle = css({
  fontSize: 'body',
  fontWeight: 'medium',
  color: 'text.secondary',
});

const emptyHint = css({
  fontSize: 'caption',
});

const centered = css({
  display: 'flex',
  justifyContent: 'center',
  paddingY: '20',
});

export interface ProductSalesFeedProps {
  products: ProductItemData[];
  loading?: boolean;
  /** Scroll infini : appelé quand la sentinelle de fin devient visible. */
  onEndReached?: () => void;
  /** Affiche le loader de page suivante en pied de liste. */
  loadingMore?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  onProductClick?: (product: ProductItemData) => void;
  onSearchSimilar?: (product: ProductItemData) => void;
  /** « Articles similaires » verrouillé par le plan (cf `ProductFeedCard.similarLocked`). */
  similarLocked?: boolean;
}

export const ProductSalesFeed: FC<ProductSalesFeedProps> = ({
  products,
  loading,
  onEndReached,
  loadingMore,
  emptyTitle: emptyTitleProp,
  emptyHint: emptyHintProp,
  onProductClick,
  onSearchSimilar,
  similarLocked = false,
}) => {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onEndReachedRef = useRef(onEndReached);
  onEndReachedRef.current = onEndReached;

  // Sentinelle de scroll infini — marge large pour précharger avant le bas
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !onEndReached) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onEndReachedRef.current?.();
      },
      { rootMargin: '800px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onEndReached]);

  if (loading && products.length === 0) {
    return (
      <div className={centered}>
        <Spinner size={26} />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className={emptyState}>
        <PackageOpen size={48} strokeWidth={1.25} />
        <span className={emptyTitle}>{emptyTitleProp ?? t('salesFeed.emptyTitle')}</span>
        <span className={emptyHint}>{emptyHintProp ?? t('salesFeed.emptyHint')}</span>
      </div>
    );
  }

  return (
    <>
      <div className={list}>
        {products.map((product) => (
          <ProductFeedCard
            key={product.id}
            product={product}
            onProductClick={onProductClick}
            onSearchSimilar={onSearchSimilar}
            similarLocked={similarLocked}
          />
        ))}
      </div>
      {onEndReached && <div ref={sentinelRef} aria-hidden className={css({ height: '1px' })} />}
      {loadingMore && (
        <div className={css({ display: 'flex', justifyContent: 'center', paddingY: '6' })}>
          <Spinner size={20} />
        </div>
      )}
    </>
  );
};
